import { Fragment, useState } from "react";
import { PageNav } from "@/components/ui/page-nav";
import { Spinner } from "@/components/ui/spinner";
import {
  ChevronRightIcon,
  CircleAlertIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Frame, FrameHeader, FramePanel } from "@/components/ui/frame";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { FunnelSummary, Hint, Metric } from "./FunnelSummary";
import { RelativeTime } from "./RelativeTime";
import {
  CREATOR_STATUS_BADGE,
  fmtClock,
  fmtDuration,
  fmtNum,
  humanize,
  pct,
} from "./format";
import type {
  BatchStatus,
  CreatorQuery,
  FleetStatus,
  RawCreatorStatus,
  SourceJob,
} from "./types";

// "No progress" needs more than one quiet refresh: with a few workers shared
// across batches, 30 s without a verdict is normal.
const STALL_WARN_MS = 5 * 60_000;

/** Per-viewer convenience: which batches were opened or closed by hand. */
const OPEN_STATE_KEY = "batch_status_open";

function loadOpenState(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(OPEN_STATE_KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

function saveOpenState(name: string, open: boolean) {
  try {
    localStorage.setItem(OPEN_STATE_KEY, JSON.stringify({ ...loadOpenState(), [name]: open }));
  } catch {
    // Storage blocked — the batch just opens by its default next time.
  }
}

interface Warning {
  variant: "destructive" | "warning";
  title: string;
  description: React.ReactNode;
}

function batchWarnings(
  batch: BatchStatus,
  fleet: FleetStatus | null | undefined,
  lastChangeAt: number | undefined,
  now: number
): Warning[] {
  const warnings: Warning[] = [];
  const { jobs, fan_out } = batch.sourcing;
  const pending = batch.status_counts.PENDING;

  if (fan_out?.status === "failed") {
    warnings.push({
      variant: "destructive",
      title: "The keyword fan-out failed",
      description: fan_out.error || "No error recorded.",
    });
  }
  for (const job of jobs.filter((j) => j.status === "failed")) {
    warnings.push({
      variant: "destructive",
      title: `Source job failed${job.keyword ? `: “${job.keyword}”` : ""}`,
      description: job.error || "No error recorded.",
    });
  }
  for (const job of jobs.filter((j) => j.stale)) {
    warnings.push({
      variant: "warning",
      title: `Source job stuck on “running”${job.keyword ? `: “${job.keyword}”` : ""}`,
      description:
        "Started more than 30 minutes ago, past the source worker's task time limit — its worker was most likely restarted or killed.",
    });
  }

  const queuedJobs = jobs.filter((j) => j.status === "queued").length;
  if (
    queuedJobs > 0 &&
    fleet?.available &&
    fleet.source.queued + fleet.source.in_flight === 0
  ) {
    warnings.push({
      variant: "warning",
      title: `${queuedJobs} keyword run${queuedJobs === 1 ? " is" : "s are"} queued, but the source queue is empty`,
      description:
        "Nothing on the source queue will pick them up — their messages were lost.",
    });
  }

  if (pending > 0 && fleet?.available && fleet.analysis.backlog === 0) {
    warnings.push({
      variant: "warning",
      title: `${fmtNum(pending)} creators PENDING with nothing in flight`,
      description: (
        <>
          The analysis queue is empty and no task is running, so nothing will
          pick them up — they were never enqueued, or their tasks were lost.
          {batch.platform === "youtube" &&
            " YouTube analysis is paused (ANALYZE_YOUTUBE unset): the worker leaves YouTube creators PENDING."}
        </>
      ),
    });
  }

  const active = isActive(batch);
  if (active && lastChangeAt && now - lastChangeAt >= STALL_WARN_MS) {
    warnings.push({
      variant: "warning",
      title: `No progress since ${fmtClock(lastChangeAt)}`,
      description: `Nothing in this batch changed across the refreshes of the last ${fmtDuration(now - lastChangeAt)}.`,
    });
  }
  return warnings;
}

/** Still sourcing or analysing. */
function isActive(batch: BatchStatus): boolean {
  return (
    batch.status_counts.PENDING > 0 ||
    batch.sourcing.jobs.some((j) => j.status === "running" || j.status === "queued")
  );
}

function SectionHint({ children }: { children: React.ReactNode }) {
  return <p className="mb-2 text-xs text-muted-foreground">{children}</p>;
}

function HeadWithHint({ children, hint, className }: { children: React.ReactNode; hint: string; className?: string }) {
  return (
    <TableHead className={className}>
      <Hint text={hint}>{children}</Hint>
    </TableHead>
  );
}

function WarningList({ warnings }: { warnings: Warning[] }) {
  return (
    <ul className="divide-y overflow-hidden rounded-lg border">
      {warnings.map((w, i) => (
        <li key={i} className="flex gap-2.5 px-3 py-2">
          {w.variant === "destructive" ? (
            <CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
          ) : (
            <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-warning" />
          )}
          <div className="min-w-0 text-sm">
            <p className={cn("font-medium", w.variant === "destructive" ? "text-destructive" : "text-warning")}>{w.title}</p>
            <p className="break-words text-xs text-muted-foreground">{w.description}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- sourcing --

function JobStatusBadge({ job }: { job: SourceJob }) {
  if (job.stale) return <Badge variant="warning">stale</Badge>;
  const attempt = job.attempt && job.attempt > 1 ? ` · try ${job.attempt}` : "";
  switch (job.status) {
    case "queued":
      return <Badge variant="ghost">queued</Badge>;
    case "running":
      return (
        <Badge variant="flat">
          <Spinner />
          running{attempt}
        </Badge>
      );
    case "done":
      return <Badge variant="success">done{attempt}</Badge>;
    case "failed":
      return <Badge variant="error">failed{attempt}</Badge>;
    default:
      return <span className="text-muted-foreground">—</span>;
  }
}

function jobDuration(job: SourceJob, now: number): string {
  if (!job.started_at) return "—";
  const started = new Date(job.started_at).getTime();
  if (job.finished_at) return fmtDuration(new Date(job.finished_at).getTime() - started);
  return job.status === "running" ? `${fmtDuration(now - started)} so far` : "—";
}

/** "2/5" keyword runs done, or null for batches without source_jobs. */
function sourcingProgress(batch: BatchStatus): { done: number; total: number; failed: number } | null {
  const { source, jobs, fan_out } = batch.sourcing;
  if (source !== "source_jobs") return null;
  return {
    done: jobs.filter((j) => j.status === "done").length,
    total: fan_out?.keywords?.length ?? jobs.length,
    failed: jobs.filter((j) => j.status === "failed").length + (fan_out?.status === "failed" ? 1 : 0),
  };
}

function SourcingTable({ batch, now }: { batch: BatchStatus; now: number }) {
  const { source, jobs, fan_out } = batch.sourcing;

  if (source === "raw_creators") {
    return (
      <div>
        <SectionHint>No source_jobs for this batch — counted from raw_creators.search_keywords.</SectionHint>
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No keyword data.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Keyword</TableHead>
                <TableHead className="text-right">Creators inserted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.keyword ?? "(none)"}>
                  <TableCell className="font-mono">{job.keyword ?? "(no keyword)"}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtNum(job.inserted)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }

  const done = jobs.filter((j) => j.status === "done").length;
  return (
    <div>
      <SectionHint>
        {fan_out
          ? `${done} of ${fan_out.keywords?.length ?? jobs.length} keywords done · fanned out ${fmtClock(fan_out.created_at)}${fan_out.finished_at ? ` · finished ${fmtClock(fan_out.finished_at)}` : ""}`
          : `${done} of ${jobs.length} done`}
      </SectionHint>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">#</TableHead>
            <TableHead>Keyword</TableHead>
            <TableHead>Status</TableHead>
            <HeadWithHint className="text-right" hint="Phyllo's total_results for the search">
              Phyllo total
            </HeadWithHint>
            <HeadWithHint className="text-right" hint="New raw_creators docs, counted in this batch">
              New
            </HeadWithHint>
            <HeadWithHint className="text-right" hint="Already in raw_creators — they stay in their original batch">
              Known
            </HeadWithHint>
            <TableHead className="text-right">Enqueued</TableHead>
            <TableHead className="text-right">Skipped</TableHead>
            <TableHead>Started</TableHead>
            <TableHead>Took</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job, i) => (
            <Fragment key={`${job.fan_out?.index ?? i}-${job.keyword}`}>
              <TableRow className={cn(job.error && "border-b-0")}>
                <TableCell className="font-mono text-muted-foreground">{job.fan_out?.index ?? i + 1}</TableCell>
                <TableCell className="font-mono">{job.keyword ?? "(no keyword)"}</TableCell>
                <TableCell><JobStatusBadge job={job} /></TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtNum(job.total_results)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtNum(job.inserted)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtNum(job.found)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtNum(job.enqueued_for_analysis)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtNum(job.skipped)}</TableCell>
                <TableCell className="whitespace-nowrap">{fmtClock(job.started_at)}</TableCell>
                <TableCell className="whitespace-nowrap">{jobDuration(job, now)}</TableCell>
              </TableRow>
              {/* The error gets its own line, in full, instead of a truncated column. */}
              {job.error && (
                <TableRow className="hover:bg-transparent">
                  <TableCell />
                  <TableCell colSpan={9} className="whitespace-normal break-words pt-0 font-mono text-xs text-destructive">
                    {job.error}
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// -------------------------------------------------------------- breakdowns --

function ShareList({
  rows,
  total,
  barClass,
  mono,
}: {
  rows: { label: string; count: number }[];
  total: number;
  barClass: string;
  mono?: boolean;
}) {
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li key={row.label} className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className={cn("min-w-0 break-words", mono && "font-mono text-xs")}>{row.label}</span>
            <span className="shrink-0 font-mono tabular-nums">
              {fmtNum(row.count)}
              <span className="ml-1 text-xs text-muted-foreground">{pct(row.count, total) ?? 0}%</span>
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-pill bg-primary/10">
            <div className={cn("h-full", barClass)} style={{ width: `${pct(row.count, total) ?? 0}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Breakdowns({ batch }: { batch: BatchStatus }) {
  return (
    <div className="grid gap-6 @2xl:grid-cols-2">
      <div>
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h4 className="text-sm font-medium">Reject reasons</h4>
          <span className="text-xs text-muted-foreground">{fmtNum(batch.status_counts.REJECTED)} rejected</span>
        </div>
        {batch.reject_reasons.length === 0 ? (
          <p className="text-sm text-muted-foreground">No rejections yet.</p>
        ) : (
          <ShareList
            rows={batch.reject_reasons.map((r) => ({ label: humanize(r.reason), count: r.count }))}
            total={batch.status_counts.REJECTED}
            barClass="bg-destructive/70"
          />
        )}
      </div>
      <div>
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h4 className="text-sm font-medium">Errors</h4>
          <span className="text-xs text-muted-foreground">{fmtNum(batch.status_counts.ERRORED)} errored</span>
        </div>
        {batch.errors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No errors.</p>
        ) : (
          <ShareList
            rows={batch.errors.map((e) => ({ label: e.message, count: e.count }))}
            total={batch.status_counts.ERRORED}
            barClass="bg-warning"
            mono
          />
        )}
      </div>
    </div>
  );
}

function KeywordQuality({ batch }: { batch: BatchStatus }) {
  const { source, jobs } = batch.sourcing;
  // With source_jobs we know the batch's own keywords; a keyword outside them
  // was added when another batch re-encountered one of these creators.
  const ownKeywords =
    source === "source_jobs" ? new Set(jobs.map((j) => j.keyword).filter(Boolean)) : null;

  return (
    <div>
      <SectionHint>A creator counts under every keyword that matched it, so rows overlap.</SectionHint>
      {batch.keywords.length === 0 ? (
        <p className="text-sm text-muted-foreground">No keyword data.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Keyword</TableHead>
              <TableHead className="text-right">Creators</TableHead>
              <TableHead className="text-right">Accepted</TableHead>
              <TableHead className="text-right">Rejected</TableHead>
              <TableHead className="text-right">Errored</TableHead>
              <TableHead className="text-right">Pending</TableHead>
              <HeadWithHint className="w-48" hint="Accepted ÷ (accepted + rejected)">
                Accept rate
              </HeadWithHint>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batch.keywords.map((k) => {
              const rate = pct(k.ACCEPTED, k.ACCEPTED + k.REJECTED);
              return (
                <TableRow key={k.keyword ?? "(none)"}>
                  <TableCell className="font-mono">
                    {k.keyword ?? "(no keyword)"}
                    {ownKeywords && k.keyword && !ownKeywords.has(k.keyword) && (
                      <Badge variant="outline" className="ml-2 font-sans">other batch</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtNum(k.total)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtNum(k.ACCEPTED)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtNum(k.REJECTED)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtNum(k.ERRORED)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtNum(k.PENDING)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 min-w-16 flex-1 overflow-hidden rounded-pill bg-primary/10">
                        <div className="h-full bg-success" style={{ width: `${rate ?? 0}%` }} />
                      </div>
                      <span className="w-10 text-right font-mono text-xs tabular-nums">
                        {rate == null ? "—" : `${rate}%`}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- creators --

function profileUrl(platform: string | null, username: string | null): string | null {
  if (!username) return null;
  if (platform === "instagram") return `https://www.instagram.com/${username}/`;
  if (platform === "tiktok") return `https://www.tiktok.com/@${username}`;
  return null;
}

function CreatorsTable({
  batch,
  onQuery,
}: {
  batch: BatchStatus;
  onQuery: (query: CreatorQuery) => void;
}) {
  const page = batch.creators;
  const query: CreatorQuery = {
    status: page.status,
    page_index: page.page_index,
    page_size: page.page_size,
  };
  const filters: (RawCreatorStatus | "ALL")[] = ["ALL", "PENDING", "ACCEPTED", "REJECTED", "ERRORED"];
  if (batch.status_counts.DUPLICATE) filters.push("DUPLICATE");

  return (
    <div>
      <Tabs
        value={page.status ?? "ALL"}
        onValueChange={(value) => onQuery({ ...query, status: value === "ALL" ? null : (value as RawCreatorStatus), page_index: 0 })}
        className="mb-2"
      >
        <TabsList variant="underline" className="w-full justify-start overflow-x-auto border-b [scrollbar-width:none]" aria-label="Filter creators by status">
          {filters.map((f) => (
            <ScopeWrapper key={f}>
              <TabsTrigger value={f} className="whitespace-nowrap">
                {f === "ALL" ? "All" : humanize(f.toLowerCase())}
                <span className="ml-1 font-mono text-xs text-muted-foreground">
                  {fmtNum(f === "ALL" ? batch.total : batch.status_counts[f])}
                </span>
              </TabsTrigger>
            </ScopeWrapper>
          ))}
        </TabsList>
      </Tabs>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Username</TableHead>
            <TableHead className="text-right">Followers</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Reason / error</TableHead>
            <TableHead className="text-right">Score</TableHead>
            <TableHead>Keywords</TableHead>
            <TableHead>Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {page.rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                No creators.
              </TableCell>
            </TableRow>
          ) : (
            page.rows.map((row) => {
              const url = profileUrl(row.platform, row.username);
              return (
                <TableRow key={row.id}>
                  <TableCell className="font-mono">
                    {url ? (
                      <a href={url} target="_blank" rel="noreferrer" className="hover:underline">
                        {row.username}
                      </a>
                    ) : (
                      row.username ?? "—"
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtNum(row.followers)}</TableCell>
                  <TableCell>
                    <Badge variant={CREATOR_STATUS_BADGE[row.status] ?? "outline"}>
                      {humanize((row.status ?? "unknown").toLowerCase())}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-72 truncate text-xs" title={row.error ?? undefined}>
                    {row.reject_reason ? humanize(row.reject_reason) : row.error ?? ""}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{row.score ?? "—"}</TableCell>
                  <TableCell className="max-w-56 truncate font-mono text-xs text-muted-foreground" title={row.keywords.join(", ")}>
                    {row.keywords.join(", ")}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    <RelativeTime at={row.last_updated} />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <PageNav
        className="mt-2 justify-end"
        page={page.page_index + 1}
        pageCount={Math.ceil(page.total / page.page_size)}
        total={page.total}
        pageSize={page.page_size}
        onPrevious={() => onQuery({ ...query, page_index: page.page_index - 1 })}
        onNext={() => onQuery({ ...query, page_index: page.page_index + 1 })}
      />
    </div>
  );
}

// ------------------------------------------------------------------ batch --

type BatchTab = "breakdown" | "sourcing" | "keywords" | "creators";

/**
 * One batch: the header and funnel are always visible; warnings and the four
 * detail tabs open below. Batches with work in flight or a warning start
 * open, finished ones start closed — and a hand toggle sticks per viewer.
 */
export function BatchSection({
  batch,
  fleet,
  lastChangeAt,
  onCreatorQuery,
  onRemove,
}: {
  batch: BatchStatus;
  fleet: FleetStatus | null | undefined;
  lastChangeAt: number | undefined;
  onCreatorQuery: (query: CreatorQuery) => void;
  onRemove: () => void;
}) {
  const now = Date.now();
  const warnings = batchWarnings(batch, fleet, lastChangeAt, now);
  const empty = batch.total === 0 && batch.sourcing.jobs.length === 0 && !batch.sourcing.fan_out;
  const active = isActive(batch);
  const sourcing = sourcingProgress(batch);
  const known = batch.sourcing.jobs.reduce((sum, j) => sum + (j.found ?? 0), 0);

  const [open, setOpen] = useState(() => loadOpenState()[batch.batch_name] ?? (active || warnings.length > 0));
  const toggle = () => {
    saveOpenState(batch.batch_name, !open);
    setOpen(!open);
  };
  // A batch with no creators of its own has nothing but its keyword runs to show.
  const [tab, setTab] = useState<BatchTab>(batch.total === 0 ? "sourcing" : "breakdown");

  const filterCreators = (status: RawCreatorStatus) => {
    onCreatorQuery({ status, page_index: 0, page_size: batch.creators.page_size });
    setTab("creators");
    if (!open) {
      saveOpenState(batch.batch_name, true);
      setOpen(true);
    }
  };
  const critical = warnings.some((w) => w.variant === "destructive");

  return (
    <Frame>
      <FrameHeader className="flex-row items-start justify-between gap-3">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          disabled={empty}
          className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-left outline-none disabled:cursor-default"
        >
          <ChevronRightIcon className={cn("mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform", open && !empty && "rotate-90", empty && "invisible")} />
          <span className="flex min-w-0 flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="break-all font-mono font-medium">{batch.batch_name}</span>
              {batch.platform && <Badge variant="outline">{batch.platform}</Badge>}
              {active && (
                <Badge variant="flat">
                  <Spinner />
                  in progress
                </Badge>
              )}
              {warnings.length > 0 && (
                <Badge variant={critical ? "error" : "warning"}>
                  {critical ? <CircleAlertIcon /> : <TriangleAlertIcon />}
                  {warnings.length} issue{warnings.length === 1 ? "" : "s"}
                </Badge>
              )}
            </span>
            {!empty && (
              <span className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                {batch.total > 0 && (
                  <>
                    <span>Inserted {fmtClock(batch.first_inserted_at)}–{fmtClock(batch.last_inserted_at)}</span>
                    <span>
                      Last verdict <RelativeTime at={batch.last_verdict_at} />
                    </span>
                  </>
                )}
                {lastChangeAt && <span>Last change seen {fmtClock(lastChangeAt)}</span>}
              </span>
            )}
          </span>
        </button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Remove ${batch.batch_name}`}
          onClick={onRemove}
        >
          <XIcon />
        </Button>
      </FrameHeader>

      {empty ? (
        <FramePanel className="text-sm text-muted-foreground">
          No creators and no source jobs for this batch — check the name.
        </FramePanel>
      ) : (
        <>
          {batch.total === 0 ? (
            // Only keyword runs so far — an all-zero funnel would just be noise.
            <FramePanel className="flex flex-wrap items-center gap-x-6 gap-y-1">
              <Metric label="Keyword runs" value={sourcing ? `${sourcing.done}/${sourcing.total}` : fmtNum(batch.sourcing.jobs.length)}>
                {sourcing && sourcing.failed > 0 && <span className="text-xs text-destructive">{sourcing.failed} failed</span>}
              </Metric>
              <p className="min-w-0 flex-1 text-sm text-muted-foreground">
                No new creators in this batch
                {known > 0 ? `: all ${fmtNum(known)} the keyword runs found were already in an earlier batch, where they stay counted.` : " yet."}
              </p>
            </FramePanel>
          ) : (
            <FramePanel>
              <FunnelSummary
                counts={batch.status_counts}
                total={batch.total}
                onFilter={filterCreators}
                extra={
                  <Metric label="Keyword runs" value={sourcing ? `${sourcing.done}/${sourcing.total}` : "—"}>
                    {sourcing && sourcing.failed > 0 && <span className="text-xs text-destructive">{sourcing.failed} failed</span>}
                  </Metric>
                }
              />
            </FramePanel>
          )}

          {open && (
            <FramePanel className="@container flex flex-col gap-4">
              {warnings.length > 0 && <WarningList warnings={warnings} />}
              <Tabs value={tab} onValueChange={(v) => setTab(v as BatchTab)} className="gap-3">
                <TabsList className="max-w-full justify-start overflow-x-auto">
                  <ScopeWrapper>
                    <TabsTrigger value="breakdown">
                      Breakdown
                    </TabsTrigger>
                  </ScopeWrapper>
                  <ScopeWrapper>
                    <TabsTrigger value="sourcing">
                      Sourcing
                      <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                        {sourcing ? `${sourcing.done}/${sourcing.total}` : fmtNum(batch.sourcing.jobs.length)}
                      </span>
                    </TabsTrigger>
                  </ScopeWrapper>
                  <ScopeWrapper>
                    <TabsTrigger value="keywords">
                      Keywords
                      <span className="ml-1.5 font-mono text-xs text-muted-foreground">{fmtNum(batch.keywords.length)}</span>
                    </TabsTrigger>
                  </ScopeWrapper>
                  <ScopeWrapper>
                    <TabsTrigger value="creators">
                      Creators
                      <span className="ml-1.5 font-mono text-xs text-muted-foreground">{fmtNum(batch.total)}</span>
                    </TabsTrigger>
                  </ScopeWrapper>
                </TabsList>
                <TabsContent value="breakdown">
                  <Breakdowns batch={batch} />
                </TabsContent>
                <TabsContent value="sourcing">
                  <SourcingTable batch={batch} now={now} />
                </TabsContent>
                <TabsContent value="keywords">
                  <KeywordQuality batch={batch} />
                </TabsContent>
                <TabsContent value="creators">
                  <CreatorsTable batch={batch} onQuery={onCreatorQuery} />
                </TabsContent>
              </Tabs>
            </FramePanel>
          )}
        </>
      )}
    </Frame>
  );
}
